import React, { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import { styles } from "../styles";
import { SectionWrapper } from "../hoc";
import { fadeIn, textVariant } from "../utils/motion";

const serviceKey = import.meta.env.VITE_EMAILJS_SERVICE_KEY;
const templateKey = import.meta.env.VITE_EMAILJS_TEMPLATE_KEY;
const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY;
const contactEmail =
  import.meta.env.VITE_CONTACT_EMAIL || "qniranjan.dev@gmail.com";

const isEmailJsConfigured =
  Boolean(serviceKey?.trim()) &&
  Boolean(templateKey?.trim()) &&
  Boolean(publicKey?.trim()) &&
  publicKey !== "REPLACE_WITH_YOUR_PUBLIC_KEY" &&
  publicKey !== "your_public_key_from_emailjs_dashboard";

const getUnavailableMessage = () =>
  import.meta.env.DEV
    ? "EmailJS is not configured. Add VITE_EMAILJS_PUBLIC_KEY to .env and restart the dev server."
    : `Unable to send your message right now. Please email me at ${contactEmail} directly.`;

const inputClass =
  "bg-[#0F0F0F] py-3.5 px-4 placeholder:text-white/40 rounded-xl text-white outline-none border border-white/10 hover:border-white/20 focus:border-[#915eff]/50 focus:ring-2 focus:ring-[#915eff]/20 transition-all font-medium w-full";

const formatSubmittedAt = () =>
  new Intl.DateTimeFormat("en-IN", {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: "Asia/Kolkata",
  }).format(new Date());

const Contact = () => {
  const formRef = useRef();
  const [form, setForm] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });

  useEffect(() => {
    if (isEmailJsConfigured) {
      emailjs.init({ publicKey });
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (value) {
      setErrors({ ...errors, [name]: "" });
    }
    if (status.message) {
      setStatus({ type: "", message: "" });
    }
  };

  const validateEmail = (email) => {
    const regex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    return regex.test(email);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    let hasErrors = false;
    const nextErrors = { name: "", email: "", message: "" };

    Object.keys(form).forEach((field) => {
      if (!form[field]) {
        nextErrors[field] = `Please write down your ${field}`;
        hasErrors = true;
      }
    });

    if (!validateEmail(form.email)) {
      nextErrors.email = "Please use a valid email address";
      hasErrors = true;
    }

    setErrors(nextErrors);
    if (hasErrors) return;

    if (!isEmailJsConfigured) {
      setStatus({
        type: "error",
        message: getUnavailableMessage(),
      });
      return;
    }

    setLoading(true);
    setStatus({ type: "", message: "" });

    const submittedAt = formatSubmittedAt();
    const formEl = formRef.current;

    if (!formEl) {
      setStatus({
        type: "error",
        message: "Something went wrong. Please try again.",
      });
      return;
    }

    formEl.date.value = submittedAt;
    formEl.time.value = submittedAt;
    formEl.reply_to.value = form.email;

    emailjs
      .sendForm(serviceKey, templateKey, formEl, { publicKey })
      .then(
        () => {
          setLoading(false);
          setStatus({
            type: "success",
            message: "Message sent! I'll get back to you soon.",
          });
          setForm({ name: "", email: "", message: "" });
        },
        (error) => {
          setLoading(false);
          console.error(error);
          setStatus({
            type: "error",
            message: import.meta.env.DEV
              ? `Failed to send. Check EmailJS template variables or email ${contactEmail} directly.`
              : `Something went wrong. Please email me at ${contactEmail} directly.`,
          });
        }
      );
  };

  return (
    <div className="w-full">
      <motion.div variants={textVariant()}>
        <p className={`${styles.sectionSubText} mb-3`}>Get in touch</p>
        <h3 className={styles.sectionHeadText}>Contact</h3>
      </motion.div>

      <motion.div
        variants={fadeIn("up", "tween", 0.15, 0.5)}
        className="mt-12 max-w-2xl mx-auto p-[1px] rounded-2xl bg-gradient-to-r from-purple-600/60 to-blue-500/60"
      >
        <div className="bg-[#0A0A0A] rounded-2xl p-6 md:p-10">
          <p className="text-white/60 text-sm text-center mb-8">
            Have a question or opportunity? Drop a message below.
          </p>

          <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="flex flex-col gap-5"
          >
            <input type="hidden" name="date" defaultValue="" />
            <input type="hidden" name="time" defaultValue="" />
            <input type="hidden" name="reply_to" defaultValue="" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <label htmlFor="name" className="flex flex-col text-left">
                <span className="text-white/90 text-sm font-medium mb-2">
                  Your Name
                </span>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="What's your name"
                  className={inputClass}
                />
                {errors.name && (
                  <p className="text-red-400 mt-1.5 text-xs">{errors.name}</p>
                )}
              </label>

              <label htmlFor="email" className="flex flex-col text-left">
                <span className="text-white/90 text-sm font-medium mb-2">
                  Your Email
                </span>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="What's your email"
                  className={inputClass}
                />
                {errors.email && (
                  <p className="text-red-400 mt-1.5 text-xs">{errors.email}</p>
                )}
              </label>
            </div>

            <label htmlFor="message" className="flex flex-col text-left">
              <span className="text-white/90 text-sm font-medium mb-2">
                Your Message
              </span>
              <textarea
                rows="5"
                id="message"
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Tell me about the role or what you'd like to discuss"
                className={`${inputClass} min-h-[150px] resize-y`}
              />
              {errors.message && (
                <p className="text-red-400 mt-1.5 text-xs">{errors.message}</p>
              )}
            </label>

            {status.message && (
              <p
                className={`text-sm text-center px-4 py-2 rounded-lg ${
                  status.type === "success"
                    ? "text-green-400 bg-green-400/10"
                    : "text-red-400 bg-red-400/10"
                }`}
              >
                {status.message}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-2 bg-gradient-to-r from-[#915eff] to-[#6366f1] py-3.5 px-8 w-full text-white font-semibold rounded-xl hover:opacity-90 transition-all disabled:opacity-60 shadow-[0_4px_24px_rgba(145,94,255,0.25)]"
            >
              {loading ? "Sending..." : "Send Message"}
            </button>
          </form>

          <p className="mt-6 text-center text-white/40 text-xs">
            Or email directly at{" "}
            <a
              href={`mailto:${contactEmail}`}
              className="text-[#915eff] hover:underline"
            >
              {contactEmail}
            </a>
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default SectionWrapper(Contact, "contact");
